/**
 * Step-up re-verification, in front of the acts ProDoc cannot take back
 * (plan D24 – D26): a link anyone can open, a retention override, a DPDP
 * erasure request.
 *
 * Copy-adapted from iTax (`repo:itax:apps/web/src/lib/stepup.ts`), which took
 * it from ProDesk, which read the contract off the running core stack: the
 * step-up surface is on **core-auth** and it is two routes that take the
 * ordinary bearer:
 *
 *     POST /api/auth/stepup/otp/send   { method }                  → sends a code
 *     POST /api/auth/stepup/challenge  { client_id, method, code } → the assertion
 *
 * `client_id` is this app's OAuth client — the audience `prodoc-service`'s
 * `require_stepup()` checks (`app/platform/stepup.py`). Locally the core stub
 * answers both and accepts `000000` (`services/core-stub/app/main.py`). The
 * challenge's answer is not published, so `readAssertion` accepts the handful
 * of names an assertion could arrive under, with or without the `data` wrapper.
 */

import { ApiError } from '@fiveox/prodoc-ui'

export const STEPUP_METHODS = ['email', 'mobile', 'totp'] as const
export type StepUpMethod = (typeof STEPUP_METHODS)[number]

export const METHOD_LABELS: Record<StepUpMethod, string> = {
  email: 'Email me a code',
  mobile: 'Text me a code',
  totp: 'Use my authenticator app',
}

/** What the dialog needs of core-auth — an interface, so a test can stand in for it. */
export interface StepUpApi {
  sendCode: (method: StepUpMethod) => Promise<void>
  /**
   * Exchange the code for an assertion. The string goes into
   * `X-StepUp-Assertion` on the one request it was minted for and is **never
   * stored** — an assertion that outlived its action would re-verify nothing.
   */
  assertStepUp: (method: StepUpMethod, code: string) => Promise<string>
}

type Fetcher = (url: string, init?: RequestInit) => Promise<Response>

/** core-auth's two routes over the host's authenticated fetcher (it owns the bearer and the refresh). */
export function createStepUpApi({ authApiUrl, clientId, fetcher }: { authApiUrl: string; clientId: string; fetcher: Fetcher }): StepUpApi {
  const base = authApiUrl.replace(/\/+$/, '')

  async function post(path: string, body: unknown): Promise<unknown> {
    let res: Response
    try {
      res = await fetcher(`${base}${path}`, {
        method: 'POST',
        // A plain object: the shell's `authed` spreads it, and a `Headers` would vanish.
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(body),
      })
    } catch {
      throw new ApiError('INTERNAL', 'Could not reach the sign-in service.', 0)
    }
    const payload = (await res.json().catch(() => null)) as { success?: boolean; data?: unknown; error?: { code?: string; message?: string } } | null
    if (!res.ok || payload?.success === false) {
      throw new ApiError(payload?.error?.code ?? 'INTERNAL', payload?.error?.message ?? 'Re-verification could not be started.', res.status)
    }
    return payload
  }

  return {
    sendCode: async (method) => {
      await post('/stepup/otp/send', { method })
    },
    assertStepUp: async (method, code) => {
      const payload = await post('/stepup/challenge', { client_id: clientId, method, code: code.trim() })
      const assertion = readAssertion(payload)
      if (!assertion) throw new ApiError('INTERNAL', 'Re-verification succeeded but returned no assertion. Please try again.', 502)
      return assertion
    },
  }
}

/** The assertion, whatever core called it. A JWT and nothing else. */
export function readAssertion(payload: unknown): string | null {
  const roots: unknown[] = [payload]
  if (payload && typeof payload === 'object' && 'data' in payload) roots.push((payload as { data: unknown }).data)
  const names = ['assertion', 'step_up_assertion', 'stepup_assertion', 'step_up_token', 'stepup_token', 'token', 'access_token']
  for (const root of roots) {
    if (!root || typeof root !== 'object') continue
    for (const name of names) {
      const value = (root as Record<string, unknown>)[name]
      if (typeof value === 'string' && value.split('.').length === 3) return value
    }
  }
  return null
}
